import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Spinner, Table } from "react-bootstrap";

import { CardTitle, ConsoleCard } from "./consoleParts";
import { OnOffButton } from "./OnOffButton";
import { SetDetail } from "./SetDetail";
import { toggleFailureMessage } from "./toggleOutcome";
import { TurnOffModal } from "./TurnOffModal";
import { blankWhenZero, classesLabel, gradesLabel, onOffLabel } from "./setLabels";
import { fetchAdminWarmUpSets, setWarmUpSetActive } from "./warmUpSetsAdminApi";

const COLUMNS = ["Key", "Name", "Grades", "Questions", "Images", "In use", "Status", "Actions"];

const LOADING = "loading";
const FAILED = "failed";
const LOADED = "loaded";

const withId = (ids, id) => new Set(ids).add(id);
const withoutId = (ids, id) => {
  const next = new Set(ids);
  next.delete(id);
  return next;
};
const toggled = (ids, id) => (ids.has(id) ? withoutId(ids, id) : withId(ids, id));

const detailIdOf = (set) => `warm-up-set-detail-${set.id}`;

const SetRow = ({ set, expanded, onToggleExpanded, busy, onOnOff }) => (
  <>
    <tr>
      <td>{set.key}</td>
      <td>{set.name}</td>
      <td>{gradesLabel(set.grade_min, set.grade_max)}</td>
      <td>{set.question_count}</td>
      <td>{blankWhenZero(set.image_count)}</td>
      <td>{blankWhenZero(set.classroom_count, classesLabel)}</td>
      <td>{onOffLabel(set.active)}</td>
      <td>
        <Button
          size="sm"
          variant="outline-primary"
          aria-expanded={expanded}
          aria-controls={detailIdOf(set)}
          onClick={onToggleExpanded}
        >
          {expanded ? "Hide questions" : "Show questions"}
        </Button>{" "}
        <OnOffButton set={set} busy={busy} onClick={onOnOff} />
      </td>
    </tr>
    {expanded && (
      <tr id={detailIdOf(set)}>
        <td colSpan={COLUMNS.length}>
          <SetDetail set={set} />
        </td>
      </tr>
    )}
  </>
);

export const SetsCard = ({ listVersion }) => {
  const [sets, setSets] = useState([]);
  const [status, setStatus] = useState(LOADING);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [busyIds, setBusyIds] = useState(() => new Set());
  const [toggleError, setToggleError] = useState(null);
  const [confirming, setConfirming] = useState(null);

  const toggleExpanded = (id) => () => setExpandedIds((prev) => toggled(prev, id));

  const changeActive = async (set, active) => {
    setToggleError(null);
    setBusyIds((prev) => withId(prev, set.id));
    try {
      const updated = await setWarmUpSetActive(set.id, active);
      setSets((prev) => prev.map((row) => (row.id === updated.id ? updated : row)));
    } catch (error) {
      setToggleError(toggleFailureMessage(error));
    } finally {
      setBusyIds((prev) => withoutId(prev, set.id));
    }
  };

  const onOnOff = (set) => () => {
    if (set.active && set.classroom_count > 0) setConfirming(set);
    else changeActive(set, !set.active);
  };

  const confirmTurnOff = () => {
    const set = confirming;
    setConfirming(null);
    changeActive(set, false);
  };

  const load = useCallback(async () => {
    setStatus(LOADING);
    try {
      const data = await fetchAdminWarmUpSets();
      setSets(data?.warmup_sets || []);
      setStatus(LOADED);
    } catch (error) {
      setStatus(FAILED);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, listVersion]);

  const isEmpty = status === LOADED && sets.length === 0;

  return (
    <ConsoleCard>
      <CardTitle>Sets</CardTitle>
      {status === FAILED && (
        <Alert variant="danger">
          Could not load the warm-up sets.{" "}
          <Button size="sm" variant="outline-danger" onClick={load}>
            Retry
          </Button>
        </Alert>
      )}
      {isEmpty && <p>No warm-up sets yet. Upload the sheet above.</p>}
      {toggleError && <Alert variant="danger">{toggleError}</Alert>}
      {(status === LOADING || (status === LOADED && !isEmpty)) && (
        <Table striped bordered hover aria-label="Warm-up sets">
          <thead>
            <tr>
              {COLUMNS.map((column) => (
                <th key={column}>{column}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {status === LOADING ? (
              <tr>
                <td colSpan={COLUMNS.length} className="text-center">
                  <Spinner animation="border" size="sm" role="status">
                    <span className="visually-hidden">Loading warm-up sets</span>
                  </Spinner>
                </td>
              </tr>
            ) : (
              sets.map((set) => (
                <SetRow
                  key={set.id}
                  set={set}
                  expanded={expandedIds.has(set.id)}
                  onToggleExpanded={toggleExpanded(set.id)}
                  busy={busyIds.has(set.id)}
                  onOnOff={onOnOff(set)}
                />
              ))
            )}
          </tbody>
        </Table>
      )}
      <TurnOffModal set={confirming} onCancel={() => setConfirming(null)} onConfirm={confirmTurnOff} />
    </ConsoleCard>
  );
};
